/**
 * OWASP Risk Rating — mirrors ThreatCat's risk_rating_service and SPICE's
 * backend `app/services/risk_rating.py` (ADR-0008).
 *
 *   OWASP:SIMPLE/L:{1|4|7}/I:{1|4|7}
 *   OWASP:ADVANCED/SL:../M:../O:../S:../ED:../EE:../A:../ID:../LC:../LI:../LAV:../LAC:../FD:../RD:../NC:../PV:..
 */

export type RatingSeverity = 'unrated' | 'note' | 'low' | 'medium' | 'high' | 'critical';
export type RatingMode = 'SIMPLE' | 'ADVANCED';

export class RiskRatingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RiskRatingError';
  }
}

const SEVERITY_BY_RISK_SUM: Record<number, RatingSeverity> = {
  2: 'note',
  3: 'low',
  4: 'medium',
  5: 'high',
  6: 'critical',
};

const LIKELIHOOD_CODES = ['SL', 'M', 'O', 'S', 'ED', 'EE', 'A', 'ID'];
const IMPACT_CODES = ['LC', 'LI', 'LAV', 'LAC', 'FD', 'RD', 'NC', 'PV'];

const VALID_SCORES: Record<RatingMode, Record<string, number[]>> = {
  SIMPLE: { L: [1, 4, 7], I: [1, 4, 7] },
  ADVANCED: {
    // Threat agent factors
    SL: [1, 3, 5, 6, 9],
    M: [1, 4, 9],
    O: [0, 4, 7, 9],
    S: [2, 4, 5, 6, 9],
    // Vulnerability factors
    ED: [1, 3, 7, 9],
    EE: [1, 3, 5, 9],
    A: [1, 4, 6, 9],
    ID: [1, 3, 8, 9],
    // Technical impact factors
    LC: [2, 6, 7, 9],
    LI: [1, 3, 5, 7, 9],
    LAV: [1, 5, 7, 9],
    LAC: [1, 7, 9],
    // Business impact factors
    FD: [1, 3, 7, 9],
    RD: [1, 4, 5, 9],
    NC: [2, 5, 7],
    PV: [3, 5, 7, 9],
  },
};

function riskLevel(score: number): number {
  if (score < 3) return 1;
  if (score < 6) return 2;
  return 3;
}

function average(scores: Record<string, number>, codes: string[]): number {
  const total = codes.reduce((sum, code) => sum + scores[code], 0);
  // Integer arithmetic keeps ROUND_HALF_UP to one decimal identical to the backend.
  return Math.round((total * 10) / codes.length) / 10;
}

function parse(vector: string): { mode: RatingMode; scores: Record<string, number> } {
  if (!vector.startsWith('OWASP:')) {
    throw new RiskRatingError("Risk rating must start with 'OWASP:' (CVSS is no longer supported)");
  }
  const [mode, ...parts] = vector.slice('OWASP:'.length).split('/');
  if ((mode !== 'SIMPLE' && mode !== 'ADVANCED') || parts.length === 0) {
    throw new RiskRatingError('Risk rating must specify SIMPLE or ADVANCED mode');
  }

  const valid = VALID_SCORES[mode];
  const scores: Record<string, number> = {};
  for (const part of parts) {
    const separator = part.indexOf(':');
    const code = separator === -1 ? part : part.slice(0, separator);
    const value = separator === -1 ? '' : part.slice(separator + 1);
    if (!code || !value || code in scores) {
      throw new RiskRatingError('Risk rating contains an invalid metric');
    }
    if (!/^\d+$/.test(value)) {
      throw new RiskRatingError(`Risk rating contains a non-numeric value for ${code}`);
    }
    const score = Number(value);
    if (!(code in valid) || !valid[code].includes(score)) {
      throw new RiskRatingError(`Risk rating contains an invalid value for ${code}`);
    }
    scores[code] = score;
  }

  if (Object.keys(scores).length !== Object.keys(valid).length) {
    throw new RiskRatingError('Risk rating does not contain the required metrics');
  }
  return { mode, scores };
}

function levelsOf(vector: string): [number, number] {
  const { mode, scores } = parse(vector);
  const [likelihood, impact] = mode === 'SIMPLE'
    ? [scores.L, scores.I]
    : [average(scores, LIKELIHOOD_CODES), average(scores, IMPACT_CODES)];
  return [riskLevel(likelihood), riskLevel(impact)];
}

/** Severity of a valid OWASP vector; throws `RiskRatingError` otherwise. */
export function calculateSeverity(vector: string): RatingSeverity {
  const [likelihood, impact] = levelsOf(vector);
  return SEVERITY_BY_RISK_SUM[likelihood + impact];
}

/** Severity of a Threat as ThreatCat computes `rating_severity`. */
export function threatSeverity(vector: string | null | undefined, threatType?: string | null): RatingSeverity {
  if (threatType === 'regulation_violation') return 'high';
  const trimmed = vector?.trim();
  if (!trimmed) return 'unrated';
  try {
    return calculateSeverity(trimmed);
  } catch {
    return 'unrated';
  }
}

/** Validation message for an editable vector input; `null` when blank or valid. */
export function ratingError(vector: string | null | undefined): string | null {
  const trimmed = vector?.trim();
  if (!trimmed) return null;
  try {
    calculateSeverity(trimmed);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid risk rating';
  }
}

export type RiskLevel = 'low' | 'medium' | 'high';
export type RiskFactor = 'likelihood' | 'impact';

export const RISK_LEVELS: RiskLevel[] = ['low', 'medium', 'high'];
const SIMPLE_SCORE: Record<RiskLevel, number> = { low: 1, medium: 4, high: 7 };

/** Likelihood/Impact levels of a valid vector (derived from averages for ADVANCED). */
export function riskLevels(vector: string | null | undefined): Record<RiskFactor, RiskLevel> | null {
  const trimmed = vector?.trim();
  if (!trimmed) return null;
  try {
    const [likelihood, impact] = levelsOf(trimmed);
    return { likelihood: RISK_LEVELS[likelihood - 1], impact: RISK_LEVELS[impact - 1] };
  } catch {
    return null;
  }
}

/**
 * Vector after picking a level in the quick Likelihood/Impact selectors.
 * Always yields `OWASP:SIMPLE`, except that an unchanged level keeps the
 * current (possibly ADVANCED) vector intact.
 */
export function withRiskLevel(vector: string | null | undefined, factor: RiskFactor, level: RiskLevel): string {
  const current = riskLevels(vector);
  if (current && current[factor] === level) return vector!.trim();
  const levels = { likelihood: 'low' as RiskLevel, impact: 'low' as RiskLevel, ...current, [factor]: level };
  return `OWASP:SIMPLE/L:${SIMPLE_SCORE[levels.likelihood]}/I:${SIMPLE_SCORE[levels.impact]}`;
}
